import { Injectable, inject } from '@angular/core';
import { UserService } from '../user/user.service';
import {
  firebaseConfig,
  firebaseVapidKey,
  firebaseMessagingSwScope,
  firebaseMessagingSwUrl,
} from '../../constants/firebase.config';
import { StorageService } from '../storage/storage.service';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import {
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
  onMessage,
} from 'firebase/messaging';
import { first, firstValueFrom } from 'rxjs';
import { StorageType } from '../storage/storage.type';

@Injectable({
  providedIn: 'root',
})
export class PushService {
  private userService = inject(UserService);
  private storage = inject(StorageService);
  private webPushInitInProgress = false;
  private webPushForegroundUnsubscribe: (() => void) | null = null;
  private permissionTapListenerAdded = false;

  // Logout cleanup
  public async unregister(): Promise<void> {
    try {
      const storedToken = await firstValueFrom(
        this.storage.getItem<{ value: string }>(StorageType.PushToken),
      );
      if (storedToken?.value) {
        await firstValueFrom(this.userService.deleteDevice(storedToken.value));
      }
    } catch {
      // empty
    } finally {
      await this.storage.removeItem(StorageType.PushToken);
      this.webPushForegroundUnsubscribe?.();
      this.webPushForegroundUnsubscribe = null;
      this.webPushInitInProgress = false;
    }
  }

  private requestPermissionOnFirstTap(): void {
    if (this.permissionTapListenerAdded) {
      return;
    }
    this.permissionTapListenerAdded = true;

    const tapEvents = ['touchend', 'click'] as const;
    const onFirstTap = () => {
      tapEvents.forEach((eventName) => document.removeEventListener(eventName, onFirstTap, true));
      this.permissionTapListenerAdded = false;

      void Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          void this.initWebPush();
        }
      });
    };
    tapEvents.forEach((eventName) => document.addEventListener(eventName, onFirstTap, true));
  }

  // Проверка API браузера + поддержка Firebase Messaging (IndexedDB и т.д.)
  private async isWebPushSupported(): Promise<boolean> {
    if (!('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window)) {
      return false;
    }

    try {
      return await isMessagingSupported();
    } catch {
      return false;
    }
  }

  // Инициализация web push
  public async initWebPush(): Promise<void> {
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return;
      }

      if (!(await this.isWebPushSupported())) {
        return;
      }

      if (this.webPushForegroundUnsubscribe || this.webPushInitInProgress) {
        return;
      }
      this.webPushInitInProgress = true;

      if (Notification.permission === 'denied') {
        this.webPushInitInProgress = false;
        return;
      }

      if (Notification.permission === 'default') {
        this.webPushInitInProgress = false;
        this.requestPermissionOnFirstTap();
        return;
      }

      const registration = await navigator.serviceWorker.register(firebaseMessagingSwUrl, {
        scope: firebaseMessagingSwScope,
        updateViaCache: 'none',
      });
      await registration.update().catch(() => undefined);
      await navigator.serviceWorker.ready;

      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
      if (await isAnalyticsSupported()) {
        getAnalytics(app);
      }
      const messaging = getMessaging(app);

      const permission = Notification.permission;

      if (permission === 'granted') {
        const token = await getToken(messaging, {
          vapidKey: firebaseVapidKey,
          serviceWorkerRegistration: registration,
        });

        this.userService
          .addDevice({ type: 'WEB', token, bundle: 'com.befree.ui' })
          .pipe(first())
          .subscribe({
            next: () => {
              void this.storage.setItem(StorageType.PushToken, { value: token });
            },
            error: (error) => {
              console.error('Ошибка сохранения токена:', error);
            },
          });
      }

      const iconUrl = `${window.location.origin}/assets/images/befree-192.png`;

      this.webPushForegroundUnsubscribe = onMessage(messaging, (payload) => {
        // В фокусе (visible) авто-уведомления от FCM обычно нет, поэтому показываем сами.
        // В фоне/свернутом состоянии — ничего не показываем из FG, чтобы не ловить дубли.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          return;
        }
        if (!payload.notification || Notification.permission !== 'granted') {
          return;
        }

        const notification = payload.notification;
        const data = (payload.data ?? {}) as Record<string, unknown>;
        const tag = payload.messageId || 'befree-notification';

        void registration
          .showNotification(notification.title || 'Уведомление', {
            body: notification.body,
            icon: notification.icon || iconUrl,
            tag,
            data,
          })
          .catch(() => undefined);
      });

      this.webPushInitInProgress = false;
    } catch {
      if (!this.webPushForegroundUnsubscribe) {
        this.webPushInitInProgress = false;
      }
    }
  }
}
